/**
 * Design Tokens - Centralized spacing, sizing, and style constants
 * Use these throughout the app for consistency
 */

// Spacing Scale (based on 8px grid)
export const spacing = {
  xs: '0.25rem',    // 4px
  sm: '0.5rem',     // 8px
  md: '1rem',       // 16px
  lg: '1.5rem',     // 24px
  xl: '2rem',       // 32px
  '2xl': '3rem',    // 48px
  '3xl': '4rem',    // 64px
};

// Layout-friendly Tailwind spacing references
const layoutSpacing = {
  pagePadding: '6',  // Tailwind px-6 for responsive page gutters
  cardPadding: '6',  // Tailwind p-6 for card padding
};

// Consistent padding classes for cards/containers
export const containerPadding = {
  tight: 'p-3',       // 12px
  default: 'p-4',     // 16px (mobile), 24px (desktop)
  comfortable: 'p-6', // 24px
  spacious: 'p-8',    // 32px
};

// Gap/spacing between elements
export const gaps = {
  tight: 'gap-2',     // 8px
  default: 'gap-4',   // 16px
  comfortable: 'gap-6', // 24px
  spacious: 'gap-8',  // 32px
};

// Border radius
export const borderRadius = {
  sm: 'rounded',      // 4px
  default: 'rounded-lg', // 8px
  large: 'rounded-xl',   // 12px
  full: 'rounded-full',
};

// Shadow elevations
export const shadows = {
  sm: 'shadow-sm',
  default: 'shadow-md',
  large: 'shadow-lg',
  xl: 'shadow-xl',
};

// Typography scales (already defined in CSS, but here for reference)
export const typography = {
  display: 'text-4xl font-bold',      // Hero headlines
  h1: 'text-3xl font-bold',           // Page titles
  h2: 'text-2xl font-semibold',       // Section headers
  h3: 'text-xl font-semibold',        // Card titles
  body: 'text-base',                  // Default text
  small: 'text-sm',                   // Secondary text
  tiny: 'text-xs',                    // Captions
};

// Button sizes
export const buttonSizes = {
  sm: 'px-3 py-1 text-sm',
  default: 'px-4 py-2 text-base',
  lg: 'px-6 py-3 text-lg',
};

// Transition durations
export const transitions = {
  fast: 'transition-all duration-150',
  default: 'transition-all duration-300',
  slow: 'transition-all duration-500',
};

// Z-index layers
export const zIndex = {
  base: 'z-0',
  dropdown: 'z-10',
  sticky: 'z-20',
  modal: 'z-40',
  modalOverlay: 'z-50',
  tooltip: 'z-60',
};

// Common component class combinations
export const card = `bg-white dark:bg-spooky-bg-medium rounded-lg shadow-md ${containerPadding.comfortable}`;
export const cardHeader = `${typography.h3} mb-4`;
export const cardBody = `${gaps.default} flex flex-col`;

// Form input styles
export const input = 'w-full px-3 py-2 border border-gray-300 dark:border-spooky-purple-mist rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary dark:bg-spooky-bg-medium dark:text-spooky-ghost-white';
export const textarea = 'w-full px-3 py-2 border border-gray-300 dark:border-spooky-purple-mist rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary dark:bg-spooky-bg-medium dark:text-spooky-ghost-white resize-none';
export const select = 'px-3 py-2 border border-gray-300 dark:border-spooky-purple-mist rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary dark:bg-spooky-bg-medium dark:text-spooky-ghost-white';

// Button variants
export const button = {
  primary: 'px-4 py-2 bg-boop-primary text-white rounded-lg hover:bg-boop-secondary transition font-medium',
  secondary: 'px-4 py-2 border border-boop-primary text-boop-primary rounded-lg hover:bg-boop-light transition font-medium',
  danger: 'px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-medium',
  ghost: 'px-4 py-2 text-gray-600 dark:text-spooky-ghost-white hover:bg-gray-100 dark:hover:bg-spooky-bg-light rounded-lg transition font-medium',
};

// Loading states
export const skeleton = 'animate-pulse bg-gray-200 dark:bg-spooky-bg-light rounded';

// Content Type Colors (based on wireframes)
export const contentTypes = {
  prompt: {
    primary: '#6366f1',     // Indigo-500
    secondary: '#8b5cf6',   // Violet-500
    light: '#f5f3ff',       // Violet-50
    darkLight: '#2d1b4e',   // Dark mode light bg
    icon: '📝',
    name: 'Prompt',
    namePlural: 'Prompts',
  },
  agent: {
    primary: '#ec4899',     // Pink-500
    secondary: '#f472b6',   // Pink-400
    light: '#fdf2f8',       // Pink-50
    darkLight: '#4a1d3a',   // Dark mode light bg
    icon: '🤖',
    name: 'AI Agent',
    namePlural: 'AI Agents',
  },
  mcp_server: {
    primary: '#06b6d4',     // Cyan-500
    secondary: '#22d3ee',   // Cyan-400
    light: '#ecfeff',       // Cyan-50
    darkLight: '#1a3a4a',   // Dark mode light bg
    icon: '🔌',
    name: 'MCP Server',
    namePlural: 'MCP Servers',
  },
};

// Badge styles
export const badge = {
  default: 'px-3 py-1 rounded-full text-sm font-semibold',
  primary: 'px-3 py-1 bg-boop-light text-boop-primary rounded-full text-sm font-semibold',
  success: 'px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded-full text-sm font-semibold',
  warning: 'px-3 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-full text-sm font-semibold',
  danger: 'px-3 py-1 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 rounded-full text-sm font-semibold',
  info: 'px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 rounded-full text-sm font-semibold',
  // Content type badges
  prompt: 'px-3 py-1 bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400 rounded-full text-sm font-semibold',
  agent: 'px-3 py-1 bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400 rounded-full text-sm font-semibold',
  mcp_server: 'px-3 py-1 bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400 rounded-full text-sm font-semibold',
};

// Content type utility functions
export const getContentTypeColor = (type) => {
  return contentTypes[type]?.primary || contentTypes.prompt.primary;
};

export const getContentTypeIcon = (type) => {
  return contentTypes[type]?.icon || contentTypes.prompt.icon;
};

export const getContentTypeName = (type, plural = false) => {
  const contentType = contentTypes[type];
  if (!contentType) return plural ? 'Prompts' : 'Prompt';
  return plural ? contentType.namePlural : contentType.name;
};

// Responsive breakpoints (for reference)
export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
};

// Aggregate default export for convenience imports
export const designTokens = {
  spacing: {
    ...layoutSpacing,
    scale: spacing,
  },
  containerPadding,
  gaps,
  borderRadius,
  shadows,
  typography,
  button,
  buttonSizes,
  transitions,
  zIndex,
  card,
  cardHeader,
  cardBody,
  input,
  textarea,
  select,
  skeleton,
  badge,
  breakpoints,
};

export default designTokens;
